// Lawn mowing mini-game: fill all grass tiles by clicking/dragging, avoid flowers
import React, { useEffect, useState } from "react";

const GRID = 6;

const buildLawn = () => {
  const tiles = [];
  for (let i = 0; i < GRID * GRID; i++) {
    const r = Math.random();
    tiles.push({
      type: r < 0.1 ? "flower" : r < 0.13 ? "rock" : "grass",
      mowed: false,
    });
  }
  return tiles;
};

const LawnGame = ({ onFinish }) => {
  const [tiles, setTiles] = useState(buildLawn);
  const [mistakes, setMistakes] = useState(0);
  const [time, setTime] = useState(15);
  const [dragging, setDragging] = useState(false);
  const [overtime, setOvertime] = useState(0);

  useEffect(() => {
    const int = setInterval(() => setTime((t) => Math.max(0, t - 1)), 1000);
    const stopDrag = () => setDragging(false);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchend", stopDrag);
    return () => {
      clearInterval(int);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchend", stopDrag);
    };
  }, []);

  const mowedCount = tiles.filter((t) => t.type === "grass" && t.mowed).length;
  const totalGrass = tiles.filter((t) => t.type === "grass").length;

  useEffect(() => {
    if (mowedCount === totalGrass || (time === 0 && mowedCount >= Math.ceil(totalGrass / 2))) {
      const pct = totalGrass ? mowedCount / totalGrass : 0;
      const rating = Math.max(1, Math.min(5, Math.round(pct * 5) - Math.min(2, mistakes) - overtime));
      onFinish({ score: mowedCount, misses: mistakes, rating });
    } else if (time === 0) {
      // Job isn't done yet: customer waits, but the rating drops
      setOvertime((o) => o + 1);
      setTime(10);
    }
    // eslint-disable-next-line
  }, [time, mowedCount]);

  const mowTile = (i) => {
    setTiles((ts) => {
      const nt = [...ts];
      const tile = nt[i];
      if (tile.type === "grass" && !tile.mowed) {
        nt[i] = { ...tile, mowed: true };
      } else if (tile.type === "flower" && !tile.mowed) {
        setMistakes((m) => m + 1);
        nt[i] = { ...tile, mowed: true };
      }
      return nt;
    });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex justify-between items-center mb-3 font-heading font-black">
        <div className="bg-green-100 border-2 border-green-300 rounded-xl px-3 py-1 text-green-800" data-testid="lawn-score">
          🌱 {mowedCount}/{totalGrass}
        </div>
        <div className="bg-rose-100 border-2 border-rose-300 rounded-xl px-3 py-1 text-rose-800">
          🌸 {mistakes}
        </div>
        <div className="bg-amber-100 border-2 border-amber-300 rounded-xl px-3 py-1 text-amber-800">
          ⏱ {time}s
        </div>
      </div>
      <p className="text-center text-sm font-semibold mb-3" data-testid="lawn-hint">
        {overtime > 0 ? (
          <span className="text-rose-700">The customer is waiting! Mow at least half the lawn to finish (−{overtime}★).</span>
        ) : (
          <span className="text-slate-600">Click &amp; drag to mow grass. Avoid the flowers 🌸 and rocks 🪨!</span>
        )}
      </p>
      <div
        className="grid gap-1 bg-amber-100 border-4 border-green-500 rounded-2xl p-2 aspect-square select-none mx-auto"
        style={{ gridTemplateColumns: `repeat(${GRID}, 1fr)`, width: "min(100%, 48vh)" }}
        onMouseDown={() => setDragging(true)}
        onTouchStart={() => setDragging(true)}
      >
        {tiles.map((t, i) => {
          const base =
            t.type === "grass"
              ? t.mowed
                ? "bg-amber-200"
                : "bg-green-500"
              : t.type === "flower"
              ? t.mowed
                ? "bg-rose-300"
                : "bg-pink-300"
              : "bg-stone-400";
          return (
            <div
              key={`tile-${i}`}
              onMouseDown={() => mowTile(i)}
              onMouseEnter={() => dragging && mowTile(i)}
              onTouchMove={(e) => {
                const touch = e.touches[0];
                const el = document.elementFromPoint(touch.clientX, touch.clientY);
                if (el && el.dataset && el.dataset.lawnIdx) {
                  mowTile(parseInt(el.dataset.lawnIdx, 10));
                }
              }}
              data-lawn-idx={i}
              data-testid={`lawn-tile-${i}`}
              className={`${base} rounded-md text-lg flex items-center justify-center aspect-square cursor-pointer transition-colors`}
            >
              {t.type === "flower" && !t.mowed && "🌸"}
              {t.type === "rock" && "🪨"}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LawnGame;
